import { ItemTypes } from '../ts/enums';

export interface PrintableScheduleItem {
  key?: string;
  name?: string;
  description?: string;
  scheduleReason?: string;
  itemType?: ItemTypes;
  itemTypeLabel?: string;
  isComplete?: boolean;
}

const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const toLines = (text: string): string[] => text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const extractChecklist = (description: string): string[] => {
    const lines = toLines(description);
    if (lines.length === 0) return [];

    return lines.map((line) => line.replace(/^[-*\d).\s]+/, '').trim()).filter((line) => line.length > 0);
};

const getItemTypeLabel = (item: PrintableScheduleItem): string => {
  if (item.itemTypeLabel) return item.itemTypeLabel;
  switch (item.itemType) {
        case ItemTypes.task:
            return 'Task';
        case ItemTypes.penalty:
            return 'Penalty';
        case ItemTypes.permission:
            return 'Permission';
        case ItemTypes.event:
            return 'Event';
        case ItemTypes.position:
            return 'Position';
        default:
          return 'Event';
    }
};

const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const printFromIframe = (html: string): void => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  if (!frameWindow) {
    document.body.removeChild(iframe);
    return;
  }

  frameWindow.document.open();
  frameWindow.document.write(html);
  frameWindow.document.close();
  setTimeout(() => {
    frameWindow.focus();
    frameWindow.print();
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 1000);
  }, 200);
};

export const printScheduleEvent = (item: PrintableScheduleItem, basketText: string): void => {
    const title = (item.name || 'Schedule Event').trim();
    const description = (item.description || '').trim();
  const primaryDetails = (description || basketText || '').trim();
    const scheduleReason = (item.scheduleReason || '').trim();
  const checklist = extractChecklist(primaryDetails);
    const generatedAt = formatDateTime(new Date());
    const itemTypeLabel = getItemTypeLabel(item);
    const bodyClass = checklist.length > 0 ? 'has-checklist' : 'no-checklist';

    const checklistHtml = checklist.length > 0
        ? `<ul class="checklist">${checklist.map((line) => `<li><span class="box"></span><span>${escapeHtml(line)}</span></li>`).join('')}</ul>`
        : '<p class="muted">No checklist items were found in this event description.</p>';

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: auto; margin: 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: "Segoe UI", Tahoma, sans-serif;
      color: #1a1a1a;
      background: #ffffff;
      line-height: 1.45;
      font-size: 12pt;
    }
    .sheet {
      width: 100%;
    }
    h1 {
      margin: 0 0 4mm;
      font-size: 20pt;
      line-height: 1.2;
    }
    h2 {
      margin: 0 0 2mm;
      font-size: 13pt;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .meta {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2mm 8mm;
      margin-bottom: 6mm;
      padding: 3mm 4mm;
      border: 1px solid #dadada;
      border-radius: 3mm;
      background: #fafafa;
      font-size: 10.5pt;
    }
    .meta strong {
      margin-right: 2mm;
    }
    .section {
      margin-top: 5mm;
      page-break-inside: avoid;
    }
    .content {
      white-space: pre-wrap;
      border-left: 2px solid #c8c8c8;
      padding-left: 3mm;
      margin: 0;
    }
    .checklist {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .checklist li {
      display: grid;
      grid-template-columns: 7mm 1fr;
      align-items: start;
      gap: 2mm;
      margin-bottom: 3mm;
      page-break-inside: avoid;
    }
    .box {
      width: 5mm;
      height: 5mm;
      border: 1px solid #333;
      margin-top: 0.8mm;
      display: inline-block;
    }
    .muted {
      color: #666;
      font-style: italic;
      margin: 0;
    }
    .footer {
      margin-top: 8mm;
      border-top: 1px solid #ddd;
      padding-top: 3mm;
      font-size: 9.5pt;
      color: #666;
    }
    @media screen {
      body {
        padding: 16px;
        background: #f3f3f3;
      }
      .sheet {
        max-width: 820px;
        margin: 0 auto;
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 24px;
      }
    }
  </style>
</head>
<body class="${bodyClass}">
  <main class="sheet">
    <h1>${escapeHtml(title)}</h1>
    <div class="meta">
      <div><strong>Type:</strong> ${escapeHtml(itemTypeLabel)}</div>
      <div><strong>Printed:</strong> ${escapeHtml(generatedAt)}</div>
      <div><strong>Status:</strong> ${item.isComplete ? 'Complete' : 'Active'}</div>
      <div><strong>Event Key:</strong> ${escapeHtml(item.key || '')}</div>
    </div>

    ${primaryDetails ? `<section class="section"><h2>Details</h2><p class="content">${escapeHtml(primaryDetails)}</p></section>` : ''}
    ${scheduleReason ? `<section class="section"><h2>Schedule Reason</h2><p class="content">${escapeHtml(scheduleReason)}</p></section>` : ''}

    <section class="section">
      <h2>Checklist</h2>
      ${checklistHtml}
    </section>

    <div class="footer">Printed from Tarlings schedule event detail view.</div>
  </main>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    printFromIframe(html);
    return;
  }

  try {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 200);
  } catch {
    printFromIframe(html);
  }
};
